import { useCallback, useMemo } from 'react';
import {
  fetchAccountUsersSearch,
  type AccountUser,
} from '@/api/useAccountUsers';
import type { SelectOption } from '@kula-ai/components';

interface UseAccountUsersFieldConfigProps {
  name: string;
  label: string;
  isMulti?: boolean;
  condition?: Record<string, unknown>;
  customOptionMapper?: (_user: AccountUser) => SelectOption;
  placeholder?: string;
  initialQuery?: string;
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
}

export const useAccountUsersFieldConfig = ({
  name,
  label,
  isMulti,
  condition,
  config,
  customOptionMapper,
  initialQuery,
  placeholder,
  fieldProps,
}: UseAccountUsersFieldConfigProps) => {
  const loadOptions = useCallback(
    async (query: string) => {
      const { users } = await fetchAccountUsersSearch(query);
      if (customOptionMapper) return users.map(customOptionMapper);
      return users.map((user) => ({
        value: String(user.id),
        label: `${user.first_name} ${user.last_name}`,
        description: user.email,
        image: user.avatar_thumb_url,
      }));
    },
    [customOptionMapper]
  );

  const fieldConfig = useMemo(() => {
    const fp: Record<string, unknown> = {
      loadOptions,
      initialQuery: initialQuery ?? 'a',
      placeholder: placeholder,
      ...(fieldProps || {}),
    };
    // Extract defaultOptions to also set as static options for value resolution
    const defaultOpts = Array.isArray(fp.defaultOptions)
      ? fp.defaultOptions
      : undefined;
    return {
      type: 'asyncSelect' as const,
      name,
      label,
      isMulti: isMulti ?? true,
      condition: condition,
      // Static options for valueToRS to resolve pre-filled values on mount
      ...(defaultOpts ? { options: defaultOpts } : {}),
      ...(config ?? {}),
      fieldProps: fp,
    };
  }, [
    condition,
    config,
    fieldProps,
    initialQuery,
    isMulti,
    label,
    loadOptions,
    name,
    placeholder,
  ]);

  return { fieldConfig };
};
